import {ActionIcon, Divider, Group, Menu, Title} from "@mantine/core";
import {
  ServiceEventTypeServiceTypeDataTable
} from "../../data-table/ServiceEventTypeServiceTypeDataTable/ServiceEventTypeServiceTypeDataTable";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {
  CompanyEntity,
  firstOrUndefined,
  getObjectId,
  ServiceEventType,
  serviceEventTypeServiceTypesSelector,
  ServiceTypeEntity,
  useGetAllServiceEventTypeServiceTypesByServiceTypeQuery
} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {ServiceEventTypeSelectModal} from "../../select/entity/ServiceEventTypeSelect/ServiceEventTypeSelect";
import {useDisclosure} from "@mantine/hooks";
import {useServiceEventTypeServiceTypeMutation} from "../../../hooks/useServiceEventTypeServiceTypeMutation";

export type ServiceEventTypeServiceTypeDataTableCardProps = CardProps & {
  company: CompanyEntity | string
  serviceType: ServiceTypeEntity | string
}

export const ServiceEventTypeServiceTypeDataTableCard = (props: ServiceEventTypeServiceTypeDataTableCardProps) => {
  const {
    company,
    serviceType,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  const [serviceEventTypeSelectOpened, serviceEventTypeSelectHandler] = useDisclosure(false);

  const selectServiceEventTypeServiceTypes = useMemo(() => {
    return serviceEventTypeServiceTypesSelector();
  }, []);

  const {
    serviceEventTypeServiceTypes,
    isServiceEventTypeServiceTypesLoading
  } = useGetAllServiceEventTypeServiceTypesByServiceTypeQuery({
    serviceTypeId: getObjectId(serviceType),
    companyId: getObjectId(company),
    regionId: regionId,
  }, {
    selectFromResult: (result) => ({
      serviceEventTypeServiceTypes: selectServiceEventTypeServiceTypes(result),
      isServiceEventTypeServiceTypesLoading: result.isLoading
    })
  });

  const {
    createServiceEventTypeServiceType,
    isCreating,
  } = useServiceEventTypeServiceTypeMutation()

  const onNewClick = useCallback(() => {
    serviceEventTypeSelectHandler.open();
  }, [serviceEventTypeSelectHandler])

  const onServiceEventTypeAddClick = useCallback((serviceEventTypes: ServiceEventType[] | undefined) => {
    const serviceEventType = firstOrUndefined(serviceEventTypes)
    if (serviceEventType) {
      createServiceEventTypeServiceType({
        serviceTypeId: getObjectId(serviceType),
        companyId: getObjectId(company),
        serviceEventType: serviceEventType.code
      }, {
        onSuccess: () => serviceEventTypeSelectHandler.close()
      })
    }
  }, [createServiceEventTypeServiceType, serviceType, company, serviceEventTypeSelectHandler])

  return (
      <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
        <Group position="apart" pb="sm">
          <Title order={4}>{t("common.entity.serviceeventtype.plural", "Service Event Types")}</Title>
          <Group spacing={4} position="left" noWrap>
            <HasAccess permissions={["servicetype_edit"]}>
              <Menu
                  width={260}
                  position="bottom-end"
                  transitionProps={{transition: "pop-top-right"}}
                  withinPortal
              >
                <Menu.Target>
                  <ActionIcon color="green">
                    <IconPlus size={16}/>
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={onNewClick}>
                    {t("common.actions.new", "New") + " " + t("common.entity.serviceeventtype", "Service Event Type")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </HasAccess>
          </Group>
        </Group>

        <Divider pb="sm"/>

        <ServiceEventTypeServiceTypeDataTable
            withBorder
            records={serviceEventTypeServiceTypes}
            fetching={isServiceEventTypeServiceTypesLoading || isCreating}
        />

        <ServiceEventTypeSelectModal
            opened={serviceEventTypeSelectOpened}
            onClose={serviceEventTypeSelectHandler.close}
            title={t("common.entity.serviceeventtype.plural", "Service Event Types")}
            onSubmit={onServiceEventTypeAddClick}
        />

      </Card>
  )
}
